import {
  CCard,
  CCardHeader,
  CCardBody,
  CRow,
  CCol,
  CSpinner,
  CAlert
} from "@coreui/react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";

import { ComparativoNota } from "./ComparativoNota";
import { LinhaTemporal } from "./LinhaTemporal";
import { PORTAL_RESPONSAVEL_PATH } from "../../../config/portalResponsavel";
import { api } from "../lib/api";
import { fmtTempo, iniciais, corAvatar } from "../lib/format";
import { useAsync } from "../lib/useAsync";

const NOTA_TIPO_LABEL = {
  rede: "nota de avaliação em rede",
  avaliacao: "nota de avaliação",
  regular: "nota regular"
};

/** Dashboard de UM aluno vinculado ao responsável logado.

    Sem lista de outros alunos da turma, sem ranking, sem nada que exponha
    dado de quem não é filho/tutelado deste responsável — só o que compara o
    aluno com MÉDIAS (turma, escola, região, rede), nunca com outro aluno
    nominalmente. Ver a decisão de produto no README do módulo.

    Não há score de engajamento aqui: o backend não expõe rota de
    engajamento para o Responsável (só filhos/execução/tempo-online/
    comparativo) — ver lib/api.js. */
export function PainelAluno({ aluno, mostrarTrocarAluno }) {
  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <CabecalhoAluno aluno={aluno} />
        {mostrarTrocarAluno && (
          <Link to={PORTAL_RESPONSAVEL_PATH} style={{ fontSize: 13 }}>
            trocar de aluno
          </Link>
        )}
      </div>
      <CRow>
        <CCol lg="5" className="mb-4">
          <ComparativoCard alunoId={aluno.id} />
        </CCol>
        <CCol lg="7" className="mb-4">
          <TempoOnlineCard alunoId={aluno.id} />
        </CCol>
      </CRow>
      <ExecucaoCard alunoId={aluno.id} />
    </div>
  );
}

PainelAluno.propTypes = {
  aluno: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    surname: PropTypes.string
  }).isRequired,
  mostrarTrocarAluno: PropTypes.bool
};

PainelAluno.defaultProps = {
  mostrarTrocarAluno: false
};

function CabecalhoAluno({ aluno }) {
  const nomeCompleto = [aluno.name, aluno.surname].filter(Boolean).join(" ");
  return (
    <div className="d-flex align-items-center" style={{ gap: 12 }}>
      <div
        className="d-flex align-items-center justify-content-center rounded-circle text-white font-weight-bold"
        style={{
          width: 44,
          height: 44,
          background: corAvatar(aluno.id),
          fontSize: 15,
          flexShrink: 0
        }}
      >
        {iniciais(nomeCompleto)}
      </div>
      <div>
        <div style={{ fontSize: 17, fontWeight: 600 }}>{nomeCompleto}</div>
        <div className="text-muted" style={{ fontSize: 12.5 }}>
          Acompanhamento de desempenho
        </div>
      </div>
    </div>
  );
}

function ComparativoCard({ alunoId }) {
  const comparativo = useAsync(() => api.alunoComparativo(alunoId), [alunoId]);

  return (
    <CCard style={{ height: "100%" }}>
      <CCardHeader>Nota comparada</CCardHeader>
      <CCardBody>
        {comparativo.loading ? (
          <Carregando />
        ) : comparativo.error ? (
          <Erro mensagem={comparativo.error} />
        ) : comparativo.data === null ? (
          <CAlert color="info" className="mb-0">
            Ainda não há dados suficientes deste aluno no painel — o comparativo
            aparece assim que a próxima sincronização processar as notas dele.
          </CAlert>
        ) : (
          <ComparativoNota
            linhas={[
              {
                label: "Aluno",
                valor: comparativo.data.aluno?.nota ?? null,
                destaque: true,
                subLabel: NOTA_TIPO_LABEL[comparativo.data.aluno?.nota_tipo]
              },
              {
                label: comparativo.data.turma?.nome ?? "Turma",
                valor: comparativo.data.turma?.nota_media ?? null
              },
              {
                label: comparativo.data.escola?.nome ?? "Escola",
                valor: comparativo.data.escola?.nota_media ?? null
              },
              {
                label: comparativo.data.regional?.nome ?? "Regional",
                valor: comparativo.data.regional?.nota_media ?? null
              },
              {
                label: comparativo.data.grupo?.nome ?? "Rede",
                valor: comparativo.data.grupo?.nota_media ?? null
              }
            ]}
          />
        )}
      </CCardBody>
    </CCard>
  );
}

function TempoOnlineCard({ alunoId }) {
  const tempo = useAsync(() => api.alunoTempoOnline(alunoId, "mes"), [alunoId]);

  const serie = (tempo.data?.serie ?? []).map(r => ({
    periodo: r.periodo,
    minutos: Math.round((r.tempo_seg ?? 0) / 60)
  }));

  return (
    <CCard style={{ height: "100%" }}>
      <CCardHeader className="d-flex align-items-center justify-content-between">
        <span>Tempo online</span>
        {!tempo.loading && !tempo.error && (
          <span style={{ fontSize: 13, fontWeight: 700 }}>
            {fmtTempo(tempo.data?.total_seg ?? 0)}
            <span
              className="text-muted font-weight-normal"
              style={{ fontSize: 11 }}
            >
              {" "}
              em {tempo.data?.dias_online ?? 0} dia
              {tempo.data?.dias_online === 1 ? "" : "s"}
            </span>
          </span>
        )}
      </CCardHeader>
      <CCardBody>
        {tempo.loading ? (
          <Carregando altura={180} />
        ) : tempo.error ? (
          <Erro mensagem={tempo.error} />
        ) : serie.length ? (
          <LinhaTemporal
            dados={serie}
            xKey="periodo"
            series={[{ key: "minutos", label: "Minutos", color: "#00968C" }]}
            altura={180}
          />
        ) : (
          <SemDados texto="Sem tempo online registrado no período." />
        )}
      </CCardBody>
    </CCard>
  );
}

function ExecucaoCard({ alunoId }) {
  const execucao = useAsync(() => api.alunoExecucao(alunoId, "mes"), [alunoId]);

  const serie = (execucao.data?.serie ?? []).map(r => ({
    periodo: r.periodo,
    iniciadas: r.iniciadas,
    concluidas: r.concluidas
  }));

  return (
    <CCard className="mb-4">
      <CCardHeader>Execução de atividades</CCardHeader>
      <CCardBody>
        {execucao.loading ? (
          <Carregando />
        ) : execucao.error ? (
          <Erro mensagem={execucao.error} />
        ) : serie.length ? (
          <LinhaTemporal
            dados={serie}
            xKey="periodo"
            series={[
              { key: "iniciadas", label: "Iniciadas", color: "#02517D" },
              { key: "concluidas", label: "Concluídas", color: "#2eb85c" }
            ]}
          />
        ) : (
          <SemDados texto="Sem execução registrada no período." />
        )}
      </CCardBody>
    </CCard>
  );
}

function Carregando({ altura = 120 }) {
  return (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{ height: altura }}
    >
      <CSpinner color="primary" size="sm" />
    </div>
  );
}

function Erro({ mensagem }) {
  return (
    <CAlert color="danger" className="mb-0">
      {mensagem}
    </CAlert>
  );
}

function SemDados({ texto }) {
  return (
    <p className="text-muted mb-0" style={{ fontSize: 13 }}>
      {texto}
    </p>
  );
}
